import { Box } from 'apps/sigma2-ui/src/components/ui/Box';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { RootCauseGraphPart } from 'apps/sigma2-ui/src/pages/FocusAreaPage/RootCauseDisparitySection';
import { getDisplayComparisonGroupNoteFromRootCauseGroupResult } from 'libs/end-to-end/src/root-cause-analysis';
import { getUniqueGroupsByNameFromRootCauseGroupResult } from 'libs/end-to-end/src/root-cause-analysis';
import { RootCauseGraphPartType } from 'libs/end-to-end/src/root-cause-analysis';
import React, { useMemo, useState } from 'react';
import { MyFocusAreaDefinition } from 'libs/end-to-end/src/my-focus-area-definition';
import { MyMetricDefinition } from 'libs/end-to-end/src/my-metric-definition';
import { RootCauseGroupResult } from 'libs/end-to-end/src/root-cause-analysis';
import { LevelDefinition } from 'libs/end-to-end/src/types/databases/dimensions/level-definition';
import { DimensionDefinition } from 'libs/end-to-end/src/types/databases/dimensions/dimension-definition';
import { ExplanatoryFactorsTable } from 'apps/sigma2-ui/src/components/ExplanatoryFactorsTable';
import { getBarChartCategories } from 'libs/end-to-end/src/oaxaca-bar-chart';
import { TranslatedContent } from 'libs/end-to-end/src/translation-content';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { CalibrationFraming } from 'libs/end-to-end/src/calibration-framing';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';

export const DeepDiveTabsGraphDisplay: React.FC<{
  calibrationFraming: CalibrationFraming;
  metricDefinition: MyMetricDefinition;
  performanceMetricDefinition: MyMetricDefinition | null;
  treatmentGroupLevelDefinition: LevelDefinition;
  referenceGroupLevelDefinition: LevelDefinition;
  dimensionDefinition: DimensionDefinition;
  carouselSwitcher: React.ReactNode;
  graphRef: React.RefObject<HTMLInputElement>;
  focusAreaDefinition: MyFocusAreaDefinition;
  groupResult: RootCauseGroupResult;
  headlineTranslatedContent: TranslatedContent;
  descriptionTranslatedContent: TranslatedContent;
  graphType: RootCauseGraphPartType;
  controlledDisparity: number | null;
}> = ({
  calibrationFraming,
  carouselSwitcher,
  graphRef,
  focusAreaDefinition,
  groupResult,
  metricDefinition,
  performanceMetricDefinition,
  treatmentGroupLevelDefinition,
  referenceGroupLevelDefinition,
  dimensionDefinition,
  headlineTranslatedContent,
  descriptionTranslatedContent,
  graphType,
  controlledDisparity,
}) => {
  const language = useLanguage();

  const uniqueGroupsByName = useMemo(() => {
    return getUniqueGroupsByNameFromRootCauseGroupResult(groupResult);
  }, [groupResult]);

  const displayComparisonGroupNote = useMemo(() => {
    return getDisplayComparisonGroupNoteFromRootCauseGroupResult(groupResult);
  }, [groupResult]);

  const resultCategories = useMemo(() => {
    return getBarChartCategories({ groupResult });
  }, [groupResult]);

  const [tooltip, setTooltip] = useState<{
    x: number;
    y: number;
    content: React.ReactNode;
  } | null>(null);

  return (
    <div>
      <Box
        as={'div'}
        display={'flex'}
        flexDirection={'col'}
        gap={'md'}
        ref={graphRef}
      >
        <Box as={'div'} display={'flex'} justifyContent={'between'} gap={'md'}>
          <span>
            <S2Typography
              variant={'headline-5-alt'}
              translatedContent={headlineTranslatedContent}
            />
          </span>

          <div className={'flex-shrink-0'}>{carouselSwitcher}</div>
        </Box>
        <Box alignSelf={'center'} w="full">
          <RootCauseGraphPart
            calibrationFraming={calibrationFraming}
            imageRef={graphRef}
            focusArea={focusAreaDefinition}
            activeGroupResult={groupResult}
            treatmentLevel={treatmentGroupLevelDefinition.columnValue}
            referenceLevel={referenceGroupLevelDefinition.columnValue}
            dimension={dimensionDefinition.columnHeaderValue}
            metricDefinition={metricDefinition}
            performanceMetricDefinition={performanceMetricDefinition}
            type={graphType}
            graphClassName={'h-[300px]'}
          />
          {groupResult.plot_type === 'BoxPlot' && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                width: '90%',
              }}
            >
              <div
                style={{ cursor: 'pointer', display: 'inline-block' }}
                onPointerEnter={(e) => {
                  setTooltip({
                    x: e.clientX,
                    y: e.clientY,
                    content: (
                      <S2Typography
                        variant={'body-6'}
                        translatedContent={language.thresholdWhatsThisDescription(
                          {
                            metricDefinition: metricDefinition,
                            controlVariables: [
                              'Tenure',
                              'Experience',
                              'Age',
                              'Job Level',
                            ],
                          },
                        )}
                      />
                    ),
                  });
                }}
                onPointerLeave={() => setTooltip(null)}
              >
                <Spacing
                  size={'sm'}
                  direction={'horizontal'}
                  className={'text-gray-600 items-center'}
                  as={'div'}
                >
                  <S2Typography
                    variant={'body-7'}
                    translatedContent={language.thresholdWhatsThisTitle()}
                  />
                </Spacing>
              </div>
            </div>
          )}

          {groupResult.plot_type === 'BoxPlot' && tooltip && (
            <div
              style={{
                position: 'fixed',
                left: tooltip.x + 10,
                top: tooltip.y + 10,
                background: 'white',
                border: '1px solid #ccc',
                padding: '6px 12px',
                borderRadius: 4,
                pointerEvents: 'auto',
                zIndex: 10,
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              }}
              onPointerLeave={() => setTooltip(null)}
            >
              {tooltip.content}
            </div>
          )}
        </Box>

        <Box
          alignSelf={'center'}
          w="full"
          display={'flex'}
          flexDirection={'col'}
          gap={'xs'}
        >
          {'decompositionType' in groupResult && (
            <span>
              <S2Typography
                translatedContent={language.oaxacaConclusion({
                  metricDefinition,
                  treatmentLevelDefinition: treatmentGroupLevelDefinition,
                  referenceLevelDefinition: referenceGroupLevelDefinition,
                  controlVariableName: groupResult.controlVariableDisplayName,
                  analysisType: groupResult.decompositionType,
                  categoriesName: resultCategories,
                  originalTreatmentLevelDisparity:
                    groupResult.plotData.decompositionType === 'Oaxaca Beta'
                      ? controlledDisparity
                      : groupResult.plotData.originalTreatmentLevelDisparity,
                })}
                variant={'headline-7-alt'}
              />
            </span>
          )}
          <S2Typography
            variant={'body-6'}
            textColorVariant="gray-900"
            translatedContent={descriptionTranslatedContent}
          />
          {uniqueGroupsByName && (
            <div className="py-2">
              <ExplanatoryFactorsTable
                selectedReferenceLevel={
                  referenceGroupLevelDefinition.displayName
                }
                groups={uniqueGroupsByName}
              />
            </div>
          )}
          {displayComparisonGroupNote && (
            <S2Typography
              variant={'body-7'}
              translatedContent={language.referenceGroupNote()}
            />
          )}
        </Box>
      </Box>
    </div>
  );
};
